/**
 * Dress the Place chrome in the merchant's own palette.
 *
 * THE SEAM THIS CLOSES. The shell reads semantic tokens (`--color-bg-surface`,
 * `--color-text-primary`, …) which resolve, by default, to the *platform's*
 * theme. The menu body inside it is painted by the merchant's template. So a
 * merchant running a near-black template got a cream header and a cream bottom
 * rail clamped around a black menu — the two halves of one screen visibly
 * belonging to different products. That is the "everything feels misplaced"
 * complaint in its purest form, and it is not fixed by picking nicer defaults:
 * the chrome has to take the merchant's colours.
 *
 * So the template's surface palette is mapped onto the same semantic tokens the
 * chrome already reads, as inline custom properties on the shell root. Nothing
 * in the chrome needs to know it is themed — it goes on reading
 * `var(--color-bg-surface)` and gets the merchant's card colour instead of the
 * platform's.
 *
 * WHY NOT buildMenuThemePageStyle. That helper exists and is right for /b and
 * /pay, which have no template — it maps a *menu theme* onto the shadcn
 * primitive layer. Here the body is already being painted by a template, and
 * theming the chrome from a different source would reintroduce the seam one
 * layer down. The chrome must match what is actually on screen beneath it.
 *
 * @see components/place/PlaceShell.tsx
 */

import type { CSSProperties } from 'react';

/** The subset of TemplateSurfacePalette the chrome needs. Structural, so this
 *  module does not depend on the templates package. */
export type PlaceChromePalette = {
  background: string;
  foreground: string;
  muted: string;
  mutedForeground: string;
  card: string;
  cardForeground: string;
  border: string;
  accent: string;
  accentForeground: string;
};

/**
 * Chrome sits on the CARD colour, not the page background: a header and a rail
 * that share the canvas colour have no edge, and the border alone is too weak a
 * signal on a dark palette. `--color-text-primary` therefore resolves to
 * `cardForeground`, the colour guaranteed to be legible on it.
 */
export function buildPlaceChromeStyle(
  palette: PlaceChromePalette | null | undefined,
): CSSProperties | undefined {
  if (!palette) return undefined;

  return {
    '--color-bg-base': palette.background,
    '--color-bg-surface': palette.card,
    '--color-bg-surface-hover': mix(palette.cardForeground, palette.card, 0.08),
    '--color-bg-sunken': palette.muted,
    '--color-border-subtle': palette.border,
    '--color-border-default': palette.border,
    '--color-text-primary': palette.cardForeground,
    '--color-text-secondary': palette.mutedForeground,
    '--color-accent': palette.accent,
    '--color-accent-text': palette.accentForeground,
  } as CSSProperties;
}

/**
 * The same palette mapped for the CONTENT region, which sits on the canvas
 * rather than on the chrome's card colour.
 *
 * WHY BOTH EXIST. Custom properties inherit their *computed* value. The chrome
 * tokens are set inline on the shell root, so every descendant — including the
 * menu body — inherits `--color-text-primary: cardForeground`. Redeclaring
 * `--foreground` deeper (which the template surface style does) does not
 * recompute it, because `--color-text-primary` was resolved at the ancestor.
 *
 * The symptom was exact and ugly: a dark template gave the chrome near-white
 * text, the body inherited that same near-white, and the menu rendered it on the
 * template's cream canvas. Every dish name, description and price on the menu
 * was invisible.
 *
 * So the body re-declares the token set against the canvas it actually sits on.
 * Text is `foreground`, not `cardForeground`.
 */
export function buildPlaceBodyStyle(
  palette: PlaceChromePalette | null | undefined,
): CSSProperties | undefined {
  if (!palette) return undefined;

  return {
    '--color-bg-base': palette.background,
    '--color-bg-surface': palette.card,
    '--color-bg-surface-hover': mix(palette.foreground, palette.background, 0.06),
    '--color-bg-sunken': palette.muted,
    '--color-border-subtle': palette.border,
    '--color-border-default': palette.border,
    '--color-text-primary': palette.foreground,
    '--color-text-secondary': palette.mutedForeground,
    '--color-accent': palette.accent,
    '--color-accent-text': palette.accentForeground,
  } as CSSProperties;
}

/**
 * `color-mix` rather than a parsed blend: template palettes arrive as whatever
 * CSS the merchant's theme produced — hex, rgb(), hsl(), oklch(), a named
 * colour — and a hand-rolled hex parser silently returns the untouched colour
 * for the rest, which is how hover states end up invisible on exactly the
 * themes that are hardest to read. The browser can mix all of them.
 */
function mix(top: string, base: string, amount: number): string {
  return `color-mix(in srgb, ${top} ${Math.round(amount * 100)}%, ${base})`;
}
